import { useState, useEffect } from 'react';
import { UserProgress } from '../services/storage';
import { vocabularyData, VocabularyWord } from '../data/vocabulary';
import { allPhrasesData, Phrase } from '../data/phrases-complete';
import { dialogs, Dialog } from '../data/dialogs';
import { ttsService } from '../services/tts';
import { sttService } from '../services/stt';
import { assessmentService } from '../services/assessment';
import { storageService } from '../services/storage';
import { initMistakeReviewService, mistakeReviewService } from '../services/mistake-review';
import { platformService } from '../services/platform';

interface SpeakingPageProps {
  user: UserProgress;
}

type PracticeMode = 'menu' | 'word' | 'sentence' | 'dialog';

export default function SpeakingPage({ user }: SpeakingPageProps) {
  const [mode, setMode] = useState<PracticeMode>('menu');
  const [currentWord, setCurrentWord] = useState<VocabularyWord | null>(null);
  const [currentPhrase, setCurrentPhrase] = useState<Phrase | null>(null);
  const [currentDialog, setCurrentDialog] = useState<Dialog | null>(null);
  const [currentLineIndex, setCurrentLineIndex] = useState(0);
  const [isListening, setIsListening] = useState(false);
  const [showResult, setShowResult] = useState(false);
  const [result, setResult] = useState<any>(null);
  const [dialogScore, setDialogScore] = useState(0);
  const [completedLines, setCompletedLines] = useState<number[]>([]);
  const [practiceHistory, setPracticeHistory] = useState<Array<{
    type: 'word' | 'sentence';
    content: string;
    score: number;
  }>>([]);

  // Initialize mistake review service
  useEffect(() => {
    initMistakeReviewService(user.id);
  }, [user.id]);

  // Check platform support on mount
  useEffect(() => {
    const platform = platformService.detect();
    if (!platform.supportsRecording) {
      // Show guide after 3 seconds (let user see the page first)
      setTimeout(() => {
        platformService.showRecordingGuide();
      }, 3000);
    }
  }, []);

  const getRandomWord = () => {
    const randomIndex = Math.floor(Math.random() * vocabularyData.length);
    setCurrentWord(vocabularyData[randomIndex]);
    setShowResult(false);
    setResult(null);
  };

  const getRandomPhrase = () => {
    const randomIndex = Math.floor(Math.random() * allPhrasesData.length);
    setCurrentPhrase(allPhrasesData[randomIndex]);
    setShowResult(false);
    setResult(null);
  };

  const getRandomDialog = () => {
    const randomIndex = Math.floor(Math.random() * dialogs.length);
    setCurrentDialog(dialogs[randomIndex]);
    setCurrentLineIndex(0);
    setDialogScore(0);
    setCompletedLines([]);
    setShowResult(false);
    setResult(null);
  };

  const handleSpeakExample = async () => {
    if (!currentWord) return;

    try {
      await ttsService.speakNormal(currentWord.example);
    } catch (error) {
      console.error('TTS error:', error);
    }
  };

  const handleStartPractice = async () => {
    // Check platform support first
    if (platformService.shouldBlockRecording()) {
      platformService.showRecordingGuide();
      return;
    }

    if (!currentWord && !currentPhrase) return;

    setIsListening(true);
    setShowResult(false);

    try {
      const transcript = await sttService.listenForPhrase();

      // Check if we got a result
      if (!transcript || transcript.trim() === '') {
        alert('没有识别到语音，请再试一次。请确保：\n1. 对着麦克风说话\n2. 说话声音足够大\n3. 麦克风权限已开启');
        return;
      }

      const targetText = mode === 'word' && currentWord ? currentWord.word :
                        mode === 'sentence' && currentPhrase ? currentPhrase.phrase : '';
      const assessment = assessmentService.assess(targetText, transcript);

      setResult({
        recognized: transcript,
        assessment,
      });
      setShowResult(true);

      // Add points
      const points = Math.round(assessment.score / 10);
      storageService.addPoints(user.id, points);

      // Get itemId before using it
      const itemId = mode === 'word' && currentWord ? currentWord.id :
                     mode === 'sentence' && currentPhrase ? currentPhrase.id : '';

      // Record mistake if score is low
      if (assessment.score < 70 && mistakeReviewService && itemId) {
        mistakeReviewService.addMistake(
          'speaking',
          itemId,
          targetText,
          transcript,
          targetText
        );
      }

      // Add to history
      setPracticeHistory([
        ...practiceHistory,
        {
          type: mode as 'word' | 'sentence',
          content: targetText,
          score: assessment.score
        },
      ]);

      // Add learning record
      if (itemId) {
        storageService.addLearningRecord(user.id, 'speaking', itemId, assessment.score, 30);
      }
    } catch (error: any) {
      console.error('Speech recognition error:', error);

      // Provide user-friendly error messages
      let errorMessage = '语音识别失败，请重试';
      if (error.message?.includes('超时')) {
        errorMessage = '录音超时了，请尽早开始说话。点击按钮后立即开始朗读。';
      } else if (error.message?.includes('权限')) {
        errorMessage = '麦克风权限被拒绝，请在浏览器设置中允许麦克风访问。';
      } else if (error.message?.includes('not-speech') || error.message?.includes('没有检测到语音')) {
        errorMessage = '没有检测到语音，请确保对着麦克风说话，声音足够大。';
      }

      alert(errorMessage);
    } finally {
      setIsListening(false);
    }
  };

  // Stop recording manually
  const handleStopRecording = () => {
    sttService.stop();
    setIsListening(false);

    // Show feedback when manually stopped
    setTimeout(() => {
      if (!showResult) {
        alert('录音已停止。如果没有看到结果，说明可能没有识别到语音，请再试一次。');
      }
    }, 500);
  };

  // Dialog practice handlers
  const handleDialogLine = async () => {
    if (!currentDialog) return;

    setIsListening(true);
    setShowResult(false);

    try {
      const currentLine = currentDialog.lines[currentLineIndex];
      const transcript = await sttService.listenForPhrase();

      // Check if we got a result
      if (!transcript || transcript.trim() === '') {
        alert('没有识别到语音，请再试一次。请确保对着麦克风说话。');
        return;
      }

      const assessment = assessmentService.assess(currentLine.text, transcript);

      setResult({
        recognized: transcript,
        assessment,
      });
      setShowResult(true);

      // Update score
      const lineScore = Math.round(assessment.score / currentDialog.lines.length);
      setDialogScore(prev => prev + lineScore);

      // Mark this line as completed
      setCompletedLines(prev => [...prev, currentLineIndex]);

      // Add points
      storageService.addPoints(user.id, Math.round(assessment.score / 10));

      // Record mistake if score is low
      if (assessment.score < 70 && mistakeReviewService) {
        mistakeReviewService.addMistake(
          'speaking',
          `dialog-${currentDialog.id}-${currentLineIndex}`,
          currentLine.text,
          transcript,
          currentLine.text
        );
      }
    } catch (error: any) {
      console.error('Speech recognition error:', error);

      // Provide user-friendly error messages
      let errorMessage = '语音识别失败，请重试';
      if (error.message?.includes('超时')) {
        errorMessage = '录音超时了，请尽早开始说话。';
      } else if (error.message?.includes('权限')) {
        errorMessage = '麦克风权限被拒绝，请在浏览器设置中允许麦克风访问。';
      }

      alert(errorMessage);
    } finally {
      setIsListening(false);
    }
  };

  const handleNextDialogLine = () => {
    if (!currentDialog) return;

    if (currentLineIndex < currentDialog.lines.length - 1) {
      setCurrentLineIndex(currentLineIndex + 1);
      setShowResult(false);
      setResult(null);
    } else {
      // Dialog completed
      setShowResult(false);
      setResult(null);
    }
  };

  // Sentence practice mode
  if (mode === 'sentence' && currentPhrase) {
    return (
      <div className="space-y-6 animate-slide-up">
        {/* Header */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => {
              setMode('menu');
              setCurrentPhrase(null);
            }}
            className="text-gray-500 hover:text-gray-700 flex items-center gap-2"
          >
            <span>←</span>
            <span>返回</span>
          </button>
          <h2 className="font-bold text-xl text-gray-800">📝 句子朗读练习</h2>
        </div>

        {/* Phrase display */}
        <div className="bg-white rounded-3xl p-8 shadow-card">
          <div className="text-center mb-6">
            <h2 className="text-3xl font-bold text-gray-800 mb-4">{currentPhrase.phrase}</h2>

            {/* Phonetics */}
            <div className="flex justify-center gap-4 mb-4">
              <div>
                <button
                  onClick={async () => {
                    const prevAccent = ttsService.getAccent();
                    ttsService.setAccent('american');
                    await ttsService.speakSlowly(currentPhrase.phrase);
                    ttsService.setAccent(prevAccent);
                  }}
                  className="text-sm text-blue-600 hover:underline mb-1 block"
                >
                  🇺🇸 美式 (慢速)
                </button>
              </div>
              <div>
                <button
                  onClick={async () => {
                    const prevAccent = ttsService.getAccent();
                    ttsService.setAccent('british');
                    await ttsService.speakSlowly(currentPhrase.phrase);
                    ttsService.setAccent(prevAccent);
                  }}
                  className="text-sm text-purple-600 hover:underline mb-1 block"
                >
                  🇬🇧 英式 (慢速)
                </button>
              </div>
            </div>

            <p className="text-lg text-gray-600 mb-4">{currentPhrase.meaning}</p>
          </div>

          {/* Usage scenario */}
          <div className="bg-blue-50 rounded-2xl p-4 mb-6">
            <p className="text-sm text-gray-600 mb-1">💡 使用场景</p>
            <p className="text-gray-800">{currentPhrase.usageScenario}</p>
          </div>

          {/* Example */}
          <div className="bg-gray-50 rounded-2xl p-4 mb-6">
            <p className="text-gray-600 mb-2">例句：<span className="italic">{currentPhrase.example}</span></p>
            {currentPhrase.exampleTranslation && (
              <p className="text-gray-500 text-sm">{currentPhrase.exampleTranslation}</p>
            )}
          </div>

          <div className="flex gap-3 justify-center">
            <button
              onClick={async () => {
                await ttsService.speakNormal(currentPhrase.example);
              }}
              className="px-6 py-3 bg-blue-100 text-blue-700 rounded-2xl font-bold hover:bg-blue-200 transition-colors"
            >
              🔊 听例句
            </button>
            <button
              onClick={getRandomPhrase}
              className="px-6 py-3 bg-green-100 text-green-700 rounded-2xl font-bold hover:bg-green-200 transition-colors"
            >
              ⏭️ 下一个
            </button>
          </div>
        </div>

        {/* Practice area */}
        {!showResult && (
          <div className="bg-gradient-to-r from-purple-100 to-pink-100 rounded-3xl p-8 text-center">
            <p className="text-xl font-bold text-gray-700 mb-4">请大声读出这个句子：</p>
            <p className="text-2xl font-bold text-primary mb-6">{currentPhrase.phrase}</p>

            <div className="flex flex-col gap-3 items-center">
              <button
                onClick={isListening ? handleStopRecording : handleStartPractice}
                className={`px-8 py-4 rounded-2xl font-bold text-lg transition-all ${
                  isListening
                    ? 'bg-red-500 text-white hover:bg-red-600 animate-pulse'
                    : 'bg-purple-500 text-white hover:opacity-90 btn-bounce'
                }`}
              >
                {isListening ? (
                  <div className="flex items-center gap-3 justify-center">
                    <span className="text-2xl">⏹️</span>
                    <span>点击停止录音</span>
                  </div>
                ) : (
                  <span>🎤 点击开始录音</span>
                )}
              </button>

              {isListening && (
                <p className="text-sm text-gray-500 animate-pulse">
                  正在录音... 最多10秒或点击停止
                </p>
              )}
            </div>
          </div>
        )}

        {/* Result */}
        {showResult && result && (
          <div className="bg-white rounded-3xl p-8 shadow-card animate-bounce-in">
            <div className="text-center mb-6">
              <div className="text-6xl mb-4 animate-celebrate">
                {result.assessment.score >= 80 ? '🎉' : result.assessment.score >= 60 ? '👍' : '💪'}
              </div>
              <p className="text-4xl font-bold text-purple-600 mb-2">{result.assessment.score} 分</p>
              <p className="text-gray-600">{result.assessment.feedback}</p>
            </div>

            <div className="space-y-3 mb-6">
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-xl">
                <span className="text-gray-600">标准：</span>
                <span className="font-bold text-gray-800">{currentPhrase.phrase}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-xl">
                <span className="text-gray-600">你说：</span>
                <span className="font-bold text-purple-600">{result.recognized}</span>
              </div>
            </div>

            <button
              onClick={getRandomPhrase}
              className="w-full py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-2xl font-bold text-lg hover:opacity-90"
            >
              下一个句子 →
            </button>
          </div>
        )}
      </div>
    );
  }

  // Handle next word/phrase
  const handleNextWord = () => {
    if (mode === 'word') {
      getRandomWord();
    } else if (mode === 'sentence') {
      getRandomPhrase();
    }
  };

  // Mode selection
  if (mode === 'menu') {
    return (
      <div className="space-y-6 animate-slide-up">
        {/* Header */}
        <div className="bg-gradient-to-r from-pink-500 to-rose-500 rounded-3xl p-8 text-white shadow-xl">
          <h1 className="text-3xl font-bold mb-2">🎤 口语练习</h1>
          <p className="text-white/80">选择练习模式,提升你的英语口语能力</p>
        </div>

        {/* Practice Mode Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={() => {
              setMode('word');
              getRandomWord();
            }}
            className="bg-gradient-to-br from-blue-400 to-blue-600 rounded-3xl p-6 text-white shadow-lg card-hover"
          >
            <span className="text-5xl mb-3 block">📚</span>
            <h3 className="font-bold text-xl mb-2">单词朗读</h3>
            <p className="text-sm text-white/80">练习单词发音</p>
            <p className="text-xs text-white/60 mt-1">适合初学者</p>
          </button>

          <button
            onClick={() => {
              setMode('sentence');
              getRandomPhrase();
            }}
            className="bg-gradient-to-br from-purple-400 to-purple-600 rounded-3xl p-6 text-white shadow-lg card-hover"
          >
            <span className="text-5xl mb-3 block">📝</span>
            <h3 className="font-bold text-xl mb-2">句子朗读</h3>
            <p className="text-sm text-white/80">练习完整句子</p>
            <p className="text-xs text-white/60 mt-1">进阶练习</p>
          </button>

          <button
            onClick={() => setMode('dialog')}
            className="bg-gradient-to-br from-green-400 to-green-600 rounded-3xl p-6 text-white shadow-lg card-hover"
          >
            <span className="text-5xl mb-3 block">💬</span>
            <h3 className="font-bold text-xl mb-2">情景对话</h3>
            <p className="text-sm text-white/80">角色扮演练习</p>
            <p className="text-xs text-white/60 mt-1">🎯 {dialogs.length} 个场景</p>
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-2xl p-4 shadow-soft text-center">
            <div className="text-3xl mb-1">📊</div>
            <p className="text-2xl font-bold text-gray-800">{practiceHistory.length}</p>
            <p className="text-sm text-gray-500">练习次数</p>
          </div>
          <div className="bg-white rounded-2xl p-4 shadow-soft text-center">
            <div className="text-3xl mb-1">✅</div>
            <p className="text-2xl font-bold text-gray-800">
              {practiceHistory.filter(p => p.score >= 80).length}
            </p>
            <p className="text-sm text-gray-500">优秀(≥80分)</p>
          </div>
          <div className="bg-white rounded-2xl p-4 shadow-soft text-center">
            <div className="text-3xl mb-1">📈</div>
            <p className="text-2xl font-bold text-gray-800">
              {practiceHistory.length > 0
                ? Math.round(practiceHistory.reduce((sum, p) => sum + p.score, 0) / practiceHistory.length)
                : 0}
            </p>
            <p className="text-sm text-gray-500">平均分</p>
          </div>
          <div className="bg-white rounded-2xl p-4 shadow-soft text-center">
            <div className="text-3xl mb-1">🔥</div>
            <p className="text-2xl font-bold text-gray-800">
              {user.points}
            </p>
            <p className="text-sm text-gray-500">总积分</p>
          </div>
        </div>

        {/* Practice history */}
        {practiceHistory.length > 0 && (
          <div className="bg-white rounded-3xl p-6 shadow-soft">
            <h3 className="font-bold text-gray-700 mb-4">最近练习记录</h3>
            <div className="space-y-2">
              {practiceHistory.slice(-5).reverse().map((item, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{item.type === 'word' ? '📚' : '📝'}</span>
                    <span className="font-medium text-gray-800">{item.content}</span>
                  </div>
                  <span className={`font-bold ${
                    item.score >= 80 ? 'text-green-600' :
                    item.score >= 60 ? 'text-blue-600' : 'text-orange-600'
                  }`}>
                    {item.score}分
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Word practice mode
  if (mode === 'word' && currentWord) {
    return (
      <div className="space-y-6 animate-slide-up">
        {/* Header */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => {
              setMode('menu');
              setCurrentWord(null);
            }}
            className="text-gray-500 hover:text-gray-700 flex items-center gap-2"
          >
            <span>←</span>
            <span>返回</span>
          </button>
          <h2 className="font-bold text-xl text-gray-800">单词朗读练习</h2>
        </div>

        {/* Word display */}
        <div className="bg-white rounded-3xl p-8 shadow-card text-center">
          <div className="text-8xl mb-4">{currentWord.image}</div>
          <h2 className="text-5xl font-bold text-gray-800 mb-2">{currentWord.word}</h2>

          {/* Phonetics with both accents */}
          <div className="flex justify-center gap-4 mb-4">
            <div>
              <button
                onClick={async () => {
                  const prevAccent = ttsService.getAccent();
                  ttsService.setAccent('american');
                  await ttsService.speakSlowly(currentWord.word);
                  ttsService.setAccent(prevAccent);
                }}
                className="text-sm text-blue-600 hover:underline mb-1 block"
              >
                🇺🇸 美式
              </button>
              <p className="text-sm font-mono text-gray-400">
                {currentWord.phoneticUS || currentWord.phonetic || '/-/'}
              </p>
            </div>
            <div className="border-l border-gray-300 pl-4">
              <button
                onClick={async () => {
                  const prevAccent = ttsService.getAccent();
                  ttsService.setAccent('british');
                  await ttsService.speakSlowly(currentWord.word);
                  ttsService.setAccent(prevAccent);
                }}
                className="text-sm text-purple-600 hover:underline mb-1 block"
              >
                🇬🇧 英式
              </button>
              <p className="text-sm font-mono text-gray-400">
                {currentWord.phoneticUK || currentWord.phonetic || '/-/'}
              </p>
            </div>
          </div>

          <p className="text-lg text-gray-600 mb-6">{currentWord.meaning}</p>

          <div className="bg-gray-50 rounded-2xl p-4 mb-6">
            <p className="text-gray-600">例句：<span className="italic">{currentWord.example}</span></p>
          </div>

          <div className="flex gap-3 justify-center">
            <button
              onClick={handleSpeakExample}
              className="px-6 py-3 bg-blue-100 text-blue-700 rounded-2xl font-bold hover:bg-blue-200 transition-colors"
            >
              🔊 听例句
            </button>
            <button
              onClick={handleNextWord}
              className="px-6 py-3 bg-green-100 text-green-700 rounded-2xl font-bold hover:bg-green-200 transition-colors"
            >
              ⏭️ 下一个
            </button>
          </div>
        </div>

        {/* Practice area */}
        {!showResult && (
          <div className="bg-gradient-to-r from-pink-100 to-purple-100 rounded-3xl p-8 text-center">
            <p className="text-xl font-bold text-gray-700 mb-4">请大声读出这个单词：</p>
            <p className="text-4xl font-bold text-primary mb-6">{currentWord.word}</p>

            <div className="flex flex-col gap-3 items-center">
              <button
                onClick={isListening ? handleStopRecording : handleStartPractice}
                className={`px-8 py-4 rounded-2xl font-bold text-lg transition-all ${
                  isListening
                    ? 'bg-red-500 text-white hover:bg-red-600 animate-pulse'
                    : 'bg-primary text-white hover:opacity-90 btn-bounce'
                }`}
              >
                {isListening ? (
                  <div className="flex items-center gap-3 justify-center">
                    <span className="text-2xl">⏹️</span>
                    <span>点击停止录音</span>
                  </div>
                ) : (
                  <span>🎤 点击开始录音</span>
                )}
              </button>

              {isListening && (
                <p className="text-sm text-gray-500 animate-pulse">
                  正在录音... 最多10秒或点击停止
                </p>
              )}
            </div>
          </div>
        )}

        {/* Result */}
        {showResult && result && (
          <div className="bg-white rounded-3xl p-8 shadow-card animate-bounce-in">
            <div className="text-center mb-6">
              <div className="text-6xl mb-4 animate-celebrate">
                {result.assessment.score >= 80 ? '🎉' : result.assessment.score >= 60 ? '👍' : '💪'}
              </div>
              <p className="text-4xl font-bold text-primary mb-2">{result.assessment.score} 分</p>
              <p className="text-gray-600">{result.assessment.feedback}</p>
            </div>

            <div className="space-y-3 mb-6">
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-xl">
                <span className="text-gray-600">标准：</span>
                <span className="font-bold text-gray-800">{currentWord.word}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-gray-50 rounded-xl">
                <span className="text-gray-600">你说：</span>
                <span className="font-bold text-primary">{result.recognized}</span>
              </div>
            </div>

            <button
              onClick={handleNextWord}
              className="w-full py-4 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-2xl font-bold text-lg hover:opacity-90"
            >
              下一个单词 →
            </button>
          </div>
        )}
      </div>
    );
  }

  // Dialog mode
  if (mode === 'dialog' && currentDialog) {
    const isComplete = currentLineIndex >= currentDialog.lines.length;
    const currentLine = currentDialog.lines[currentLineIndex];

    return (
      <div className="space-y-6 animate-slide-up">
        {/* Header */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => {
              setMode('menu');
              setCurrentDialog(null);
            }}
            className="text-gray-500 hover:text-gray-700 flex items-center gap-2"
          >
            <span>←</span>
            <span>返回</span>
          </button>
          <h2 className="font-bold text-xl text-gray-800">💬 对话练习</h2>
        </div>

        {!isComplete ? (
          <>
            {/* Dialog info */}
            <div className="bg-white rounded-3xl p-6 shadow-card">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-bold text-lg text-gray-800">{currentDialog.title}</h3>
                  <p className="text-sm text-gray-500">场景：{currentDialog.scene}</p>
                </div>
                <div className="text-right">
                  <div className="text-sm text-gray-500">进度</div>
                  <div className="font-bold text-primary">
                    {currentLineIndex + 1} / {currentDialog.lines.length}
                  </div>
                </div>
              </div>

              {/* Progress bar */}
              <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
                <div
                  className="bg-primary h-2 rounded-full transition-all"
                  style={{ width: `${((currentLineIndex + 1) / currentDialog.lines.length) * 100}%` }}
                />
              </div>

              {/* Current line */}
              <div className="bg-blue-50 rounded-2xl p-6 mb-4">
                <div className="flex items-start gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-blue-500 text-white flex items-center justify-center font-bold">
                    {currentLine.speaker}
                  </div>
                  <div className="flex-1">
                    <div className="text-sm text-gray-500 mb-1">请朗读：</div>
                    <div className="text-2xl font-bold text-gray-800">{currentLine.text}</div>
                    {currentLine.translation && (
                      <div className="text-sm text-gray-600 mt-2">💡 {currentLine.translation}</div>
                    )}
                  </div>
                </div>

                <button
                  onClick={async () => {
                    await ttsService.speakNormal(currentLine.text);
                  }}
                  className="text-sm text-blue-600 hover:underline"
                >
                  🔊 听示范
                </button>
              </div>

              {/* Previous lines */}
              {completedLines.length > 0 && (
                <div className="space-y-2 mb-4">
                  <div className="text-sm text-gray-500 font-bold">对话记录：</div>
                  {currentDialog.lines.slice(0, currentLineIndex).map((line, idx) => (
                    <div key={idx} className="flex items-start gap-2 text-sm">
                      <span className="font-bold text-gray-600">{line.speaker}:</span>
                      <span className="text-gray-800">{line.text}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Practice area */}
            {!showResult && (
              <div className="bg-gradient-to-r from-green-100 to-blue-100 rounded-3xl p-8 text-center">
                <p className="text-xl font-bold text-gray-700 mb-4">请朗读上面的句子：</p>

                <div className="flex flex-col gap-3 items-center">
                  <button
                    onClick={isListening ? handleStopRecording : handleDialogLine}
                    className={`px-8 py-4 rounded-2xl font-bold text-lg transition-all ${
                      isListening
                        ? 'bg-red-500 text-white hover:bg-red-600 animate-pulse'
                        : 'bg-green-500 text-white hover:opacity-90 btn-bounce'
                    }`}
                  >
                    {isListening ? (
                      <div className="flex items-center gap-3 justify-center">
                        <span className="text-2xl">⏹️</span>
                        <span>点击停止录音</span>
                      </div>
                    ) : (
                      <span>🎤 点击开始录音</span>
                    )}
                  </button>

                  {isListening && (
                    <p className="text-sm text-gray-500 animate-pulse">
                      正在录音... 最多10秒或点击停止
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Result */}
            {showResult && result && (
              <div className="bg-white rounded-3xl p-8 shadow-card animate-bounce-in">
                <div className="text-center mb-6">
                  <div className="text-6xl mb-4 animate-celebrate">
                    {result.assessment.score >= 80 ? '🎉' : result.assessment.score >= 60 ? '👍' : '💪'}
                  </div>
                  <p className="text-4xl font-bold text-green-600 mb-2">{result.assessment.score} 分</p>
                  <p className="text-gray-600">{result.assessment.feedback}</p>
                </div>

                <div className="space-y-3 mb-6">
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-xl">
                    <span className="text-gray-600">标准：</span>
                    <span className="font-bold text-gray-800">{currentLine.text}</span>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-gray-50 rounded-xl">
                    <span className="text-gray-600">你说：</span>
                    <span className="font-bold text-green-600">{result.recognized}</span>
                  </div>
                </div>

                <button
                  onClick={handleNextDialogLine}
                  className="w-full py-4 bg-gradient-to-r from-green-500 to-blue-500 text-white rounded-2xl font-bold text-lg hover:opacity-90"
                >
                  {currentLineIndex < currentDialog.lines.length - 1 ? '下一句 →' : '完成对话 →'}
                </button>
              </div>
            )}
          </>
        ) : (
          /* Dialog complete */
          <div className="bg-white rounded-3xl p-8 shadow-card text-center animate-bounce-in">
            <div className="text-8xl mb-6 animate-celebrate">🎉</div>
            <h2 className="text-3xl font-bold text-gray-800 mb-4">对话完成！</h2>
            <div className="text-6xl font-bold text-primary mb-4">{dialogScore} 分</div>
            <p className="text-gray-600 mb-6">你成功完成了"{currentDialog.title}"对话练习</p>

            {/* Keywords */}
            <div className="bg-blue-50 rounded-2xl p-4 mb-6">
              <div className="text-sm text-gray-500 mb-2">关键词汇：</div>
              <div className="flex flex-wrap gap-2 justify-center">
                {currentDialog.keywords.map((keyword, idx) => (
                  <span key={idx} className="px-3 py-1 bg-white rounded-full text-sm text-gray-700">
                    {keyword}
                  </span>
                ))}
              </div>
            </div>

            <button
              onClick={getRandomDialog}
              className="px-8 py-4 bg-gradient-to-r from-green-500 to-blue-500 text-white rounded-2xl font-bold text-lg hover:opacity-90"
            >
              练习下一个对话 →
            </button>
          </div>
        )}
      </div>
    );
  }

  // Dialog mode selection
  if (mode === 'dialog' && !currentDialog) {
    return (
      <div className="space-y-6 animate-slide-up">
        <div className="flex items-center justify-between">
          <button onClick={() => setMode('menu')} className="text-gray-500 hover:text-gray-700 flex items-center gap-2">
            <span>←</span>
            <span>返回</span>
          </button>
          <h2 className="font-bold text-xl text-gray-800">💬 选择对话场景</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {dialogs.map((dialog) => (
            <button
              key={dialog.id}
              onClick={() => {
                setCurrentDialog(dialog);
                setCurrentLineIndex(0);
                setDialogScore(0);
                setCompletedLines([]);
              }}
              className="bg-white rounded-2xl p-6 shadow-soft text-left hover:shadow-lg transition-all"
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-bold text-lg text-gray-800">{dialog.title}</h3>
                <span className={`px-2 py-1 rounded-full text-xs ${
                  dialog.level === 'beginner' ? 'bg-green-100 text-green-700' :
                  dialog.level === 'intermediate' ? 'bg-blue-100 text-blue-700' :
                  'bg-purple-100 text-purple-700'
                }`}>
                  {dialog.level === 'beginner' ? '初级' : dialog.level === 'intermediate' ? '中级' : '高级'}
                </span>
              </div>
              <p className="text-sm text-gray-500 mb-2">场景：{dialog.scene}</p>
              <div className="text-xs text-gray-400">{dialog.lines.length} 句对话</div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return null;
}